import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DaumPostcodeEmbed from "react-daum-postcode";
import { useMediaQuery } from "hooks/utils/useMediaQuery";
import Radio from "./common/Radio";
import Input from "./common/Input";
import Button from "./common/Button";
import AddressBook from "./AddressBook";
import { useMorningAuthStore } from "../../../../store/useMorningAuthStore";
import { ReactComponent as ToolTip } from "assets/morning/icon/toolTip.svg";
import { ReactComponent as Close } from "assets/morning/icon/close.svg";
import { useMasterAuthStore } from "../../../../store/useMasterAuthStore";
import { createPortal } from "react-dom";
import { useGetOrderSearch } from "../../../../hooks/morning/useGetOrderSearch";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "components/ui/dialog";
import { useAppContext } from "context/AppContext";
import OrderGuide from "../-pages/customer/OrderGuide";
import { AddressItem, usePostAddressBook } from "hooks/morning/usePostAddressBook";

const shippingAndPaymentInfo = (
    <>
        <div className="absolute -top-2 left-6 w-3 h-3 bg-white border-l border-t border-gray-300 rotate-45 responsive700:hidden" />
        <p className="text-[20px] text-new mb-2">발송구분</p>
        <p>
            - 당사픽업접수 : 등록된 (사업장) 주소지에서 발송 <br />
            - 외부업체픽업접수 : 다른 업체 (등록되지 않은 사업장)에서 발송
            <br />
            &nbsp;&nbsp;&nbsp;ex) 접수처 A, 출발지 B, 도착지 C<br />
            &nbsp;&nbsp;&nbsp;① 출발지 B ➝ 도착지 A<br />
            &nbsp;&nbsp;&nbsp;② 출발지 B ➝ 도착지 C <br />* 아파트, 개인자택에서 물품픽업은 안 됩니다.
        </p>
        <p className="text-[20px] text-new my-2">지불방법</p>
        <p>
            - 당사픽업접수
            <br /> &nbsp;&nbsp;&nbsp;① 신용(월청구) : 등록된 사업자로 월 청구 <br />
            &nbsp;&nbsp;&nbsp;② 현금지급 : 픽업담당자에게 현금지급
            <br /> &nbsp;&nbsp;&nbsp;③ 착불(도착지) : 수신처에서 운임지급
            <br /> &nbsp;&nbsp;&nbsp;④ 요금처(제3자) : 접수처 및 도착지 외 다른 곳에서 운임 지급
        </p>
        <p className="mt-2">
            - 외부업체픽업접수
            <br /> &nbsp;&nbsp;&nbsp;① 접수처(월청구) : 접수처(등록된 사업자) 월 청구 <br />
            &nbsp;&nbsp;&nbsp;② 출발지 : 출발지에서 운임 지급 <br />
            &nbsp;&nbsp;&nbsp;③ 도착지 : 도착지에서 운임 지급
            <br /> &nbsp;&nbsp;&nbsp;④ 요금처(제3자) : 접수처, 출발지, 도착지 외 다른 곳에서 운임 지급
        </p>
    </>
);
type PayMethodProps = {
    modify?: boolean;
    data: any;
    setData: Function;
    id?: string;
    master?: boolean;
    errorMessages?: any;
    isSubmitting?: boolean;
    infoData?: any;
    onPickupChange?: (pickup: number) => void;
};
const PayMethod = forwardRef(
    (
        {
            modify = false,
            data,
            setData,
            id,
            master,
            errorMessages,
            isSubmitting,
            infoData,
            onPickupChange,
        }: PayMethodProps,
        ref,
    ) => {
        const [searchParams] = useSearchParams();
        const isDevMode = searchParams.get("dev") === "true";
        const now = new Date();
        const isBeforeSix = now.getHours() < 18 || isDevMode;

        const containerRef = useRef<HTMLDivElement>(null);
        const thirdPartyRefs = useRef<(HTMLDivElement | null)[]>([]);

        const c_code = useMorningAuthStore(state => state.c_code ?? "");
        const month_yn = useMorningAuthStore(state => state.c_month_yn ?? "");
        const masterStoreCCode = useMasterAuthStore(state => state.c_code ?? "");
        const master_month_yn = useMasterAuthStore(state => state.c_month_yn ?? "");
        const c_month_yn = master ? master_month_yn : month_yn;

        const [showPostcode, setShowPostcode] = useState(false);
        const [showAddressBook, setShowAddressBook] = useState(false);
        const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
        const [thirdPartyFocusedIndex, setThirdPartyFocusedIndex] = useState(-1);
        const [thirdPartyIsFocused, setThirdPartyIsFocused] = useState(false);
        const [showOrderGuide, setShowOrderGuide] = useState(false);
        const { isApp } = useAppContext();
        // const isDevDomain = useIsDevelopment(); // 개발 운영 모드
        const { data: thirdPartySearchData } = useGetOrderSearch(data.name, { enabled: data.payment !== 4 });
        const [addressBookSearchData, setAddressBookSearchData] = useState<AddressItem[]>([]);
        const { mutate } = usePostAddressBook((data: AddressItem[]) => {
            setAddressBookSearchData(data);
        });

        const drawerTriggerRef = useRef<HTMLButtonElement | null>(null);
        const responsive700 = useMediaQuery(700);

        const pickupOptions = [{ label: "당사픽업접수", value: 1 }];
        if (isBeforeSix && infoData?.c_no_yn === "신용") {
            if (!master) {
                if (c_code) {
                    // if (infoData?.c_code) {
                    pickupOptions.push({ label: "외부업체픽업접수", value: 2 });
                }
            } else {
                if (masterStoreCCode) {
                    pickupOptions.push({ label: "외부업체픽업접수", value: 2 });
                }
            }
        }

        // console.log(c_month_yn); Y

        // const alertShown = useRef(false);
        // 웹뷰 디버깅용 코드
        // useEffect(() => {
        //     if (isApp) {
        //         const auth = useMorningAuthStore.getState();
        //         // alert(`isApp: ${isApp} \n c_code: ${infoData?.c_code} \n monthYn: ${infoData?.c_month_yn}`);
        //         // if (!alertShown.current) {
        //         // alertShown.current = true;
        //         // alert(`info: ${JSON.stringify(info)}`);
        //         // alert(`PayMethod: ${JSON.stringify(useMorningAuthStore.getState())}`);
        //         // alert(`auth.c_code: ${auth.c_code} \n auth.c_month_yn: ${auth.c_month_yn}`);
        //         // }
        //     }
        // }, [isApp]);

        useImperativeHandle(ref, () => ({
            validate: () => {
                const newErrors: { [key: string]: boolean } = {};

                const requiredFields = ["count"];
                if (data.payment === 4) {
                    requiredFields.push("name", "tel", "manager", "postcode", "address");
                }

                requiredFields.forEach(field => {
                    const value = data[field];
                    if (typeof value === "string") {
                        if (!value.trim()) {
                            newErrors[field] = true;
                        }
                    } else if (value === undefined || value === null) {
                        newErrors[field] = true;
                    }
                });

                setErrors(newErrors);
                return Object.keys(newErrors).length === 0;
            },
            scrollIntoView: () => {
                containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            },
        }));

        const paymentOptions = master
            ? masterStoreCCode
                ? data.pickup === 1
                    ? [
                          ...(infoData?.c_no_yn === "신용" ? [{ label: "신용(월 청구)", value: 1 }] : []),
                          { label: "현금지급", value: 2 },
                          { label: "착불(도착지)", value: 3 },
                          { label: "요금처(제3자)", value: 4 },
                      ]
                    : [
                          { label: "접수처(월 청구)", value: 1 },
                          { label: "출발지", value: 2 },
                          { label: "도착지", value: 3 },
                          { label: "요금처(제3자)", value: 4 },
                      ]
                : [
                      { label: "접수처(현불)", value: 1 },
                      { label: "도착지(착불)", value: 2 },
                  ]
            : c_code
              ? data.pickup === 1
                  ? [
                        ...(infoData?.c_no_yn === "신용" ? [{ label: "신용(월 청구)", value: 1 }] : []),
                        { label: "현금지급", value: 2 },
                        { label: "착불(도착지)", value: 3 },
                        { label: "요금처(제3자)", value: 4 },
                    ]
                  : [
                        { label: "접수처(월 청구)", value: 1 },
                        { label: "출발지", value: 2 },
                        { label: "도착지", value: 3 },
                        { label: "요금처(제3자)", value: 4 },
                    ]
              : [
                    { label: "접수처(현불)", value: 1 },
                    { label: "도착지(착불)", value: 2 },
                ];

        const handleAddress = (data: any) => {
            let fullAddress = data.roadAddress || data.jibunAddress;

            const extras: string[] = [];

            if (data.bname) extras.push(data.bname);

            if (extras.length > 0) {
                fullAddress += ` (${extras.join(", ")})`;
            }

            setData((prev: any) => ({
                ...prev,
                postcode: data.zonecode,
                address: fullAddress,
            }));

            setErrors(prev => ({
                ...prev,
                postcode: false,
                address: false,
            }));

            setShowPostcode(false);
        };

        const handleAddressSelect = (item: any) => {
            setData({
                ...data,
                name: item.a_company || "",
                tel: item.a_tel || "",
                manager: item.a_name || "",
                postcode: item.a_post || "",
                address: item.a_address || "",
                addressDetail: item.a_address_detail || "",
                s_pay_company_code: item.a_code || "",
            });

            setErrors(prev => ({
                ...prev,
                name: false,
                tel: false,
                manager: false,
                postcode: false,
                address: false,
                addressDetail: false,
            }));
            setShowAddressBook(false);
        };

        const isHandlingThirdPartyKey = useRef(false);

        const handleThirdPartyKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (isHandlingThirdPartyKey.current) return;
            isHandlingThirdPartyKey.current = true;
            setTimeout(() => {
                isHandlingThirdPartyKey.current = false;
            }, 50);

            if (!thirdPartySearchData || thirdPartySearchData.length === 0) return;

            if (e.key === "ArrowDown") {
                e.preventDefault();
                e.stopPropagation();
                setThirdPartyFocusedIndex(prev => {
                    if (prev === -1) return 0;
                    if (prev < thirdPartySearchData.length - 1) return prev + 1;
                    return 0;
                });
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                e.stopPropagation();
                setThirdPartyFocusedIndex(prev => {
                    if (prev > 0) return prev - 1;
                    return thirdPartySearchData.length - 1;
                });
            } else if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                if (thirdPartyFocusedIndex >= 0 && thirdPartyFocusedIndex < thirdPartySearchData.length) {
                    const selected = thirdPartySearchData[thirdPartyFocusedIndex];
                    // console.log('thirdParty selected',selected);
                    setData({
                        ...data,
                        name: selected.C_Company,
                        tel: selected.c_tel,
                        postcode: selected.c_post,
                        address: selected.C_ADDR1,
                        addressDetail: selected.C_ADDR2,
                    });
                    setThirdPartyIsFocused(false);
                    setThirdPartyFocusedIndex(-1);
                }
            } else if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                setThirdPartyIsFocused(false);
                setThirdPartyFocusedIndex(-1);
            }
        };

        const onClickShippingAndPaymentToolTip = () => {
            if (responsive700) drawerTriggerRef.current?.click();
        };

        useEffect(() => {
            if (thirdPartyFocusedIndex >= 0 && thirdPartyRefs.current[thirdPartyFocusedIndex]) {
                thirdPartyRefs.current[thirdPartyFocusedIndex]?.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                });
            }
        }, [thirdPartyFocusedIndex]);

        // console.log(data.pickup);
        return (
            <>
                <div
                    ref={containerRef}
                    id={id}
                    className={`bg-white ${modify ? "w-full max-w-[680px] pt-2" : "w-full py-10"} rounded-2xl px-10 responsive1200:p-[16px] responsive1200:rounded-none responsive1200:border-t responsive1200:border-t-[12px] responsive1200:border-[#f4f4f5]`}
                >
                    <div className="border-b-[2px] border-black flex items-center pb-2 responsive450:flex-col-reverse">
                        <div className="flex items-center responsive450:self-start">
                            <p className="text-[28px] font-bold mr-3 responsive1200:text-[20px]">발송 및 지불 방법</p>
                            <div tabIndex={-1} className="relative group">
                                <ToolTip className="cursor-pointer" onClick={onClickShippingAndPaymentToolTip} />
                                {/* <div className="hidden group-hover:block w-[540px] absolute top-full -left-4 mt-2 bg-white border border-gray-300 rounded-lg shadow-md px-4 py-3 text-[17px] text-[#51535B] z-10 responsive700:group-hover:hidden"> */}
                                <div className="hidden group-hover:block w-[430px] absolute top-full -left-4 mt-2 bg-white border border-gray-300 rounded-lg shadow-md px-4 py-3 text-[17px] text-[#51535B] z-10 responsive700:group-hover:hidden">
                                    {shippingAndPaymentInfo}
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={() => {
                                if (isApp) {
                                    setShowOrderGuide(true);
                                } else {
                                    window.open("/order-guide", "_blank");
                                }
                            }}
                            className="border border-new text-new py-[7px] px-[14px] rounded-lg ml-auto responsive450:w-full responsive450:mb-3"
                        >
                            인터넷오더접수 가이드
                        </button>
                    </div>
                    <div className="border-b py-[33px] flex items-center responsive1200:flex-col responsive1200:items-start responsive1200:gap-[18px] responsive1200:py-[20px]">
                        <div className={`text-[18px] font-[500] ${modify ? "w-[120px]" : "w-[160px]"}`}>
                            발송구분 <span className="text-new text-[16px] font-normal">*</span>
                        </div>
                        <div className="flex items-center gap-8 responsive1200:w-full ">
                            <Radio
                                gap="gap-20"
                                name="pickup"
                                options={pickupOptions}
                                selected={data.pickup}
                                onChange={(val: any) => {
                                    setData((prev: any) => ({ ...prev, pickup: val, payment: 1 }));
                                    // 발송구분을 직접 바꿨을 때만 출발지/도착지를 초기화한다
                                    onPickupChange && onPickupChange(val);
                                }}
                                disabled={modify}
                                containerClassName="responsive1200:w-full responsive1200:grid responsive1200:grid-cols-2 responsive1200:gap-0"
                            />
                        </div>
                    </div>
                    <div className="border-b py-[33px] flex items-center responsive1200:flex-col responsive1200:items-start responsive1200:gap-[18px] responsive1200:py-[20px]">
                        <div className={`text-[18px] font-[500] ${modify ? "w-[120px]" : "w-[160px]"}`}>
                            지불방법 <span className="text-new text-[16px] font-normal">*</span>
                        </div>
                        <div className="flex items-center gap-8 responsive1200:w-full">
                            <Radio
                                twoLine={modify}
                                className="w-36 whitespace-nowrap"
                                gap="gap-24"
                                name="payment"
                                options={paymentOptions}
                                selected={data.payment}
                                onChange={(val: any) => setData((prev: any) => ({ ...prev, payment: val }))}
                                containerClassName="responsive1200:w-full responsive1200:grid responsive1200:grid-cols-2 responsive1200:gap-0"
                            />
                        </div>
                    </div>
                    <div
                        className={`flex gap-x-3 w-full mt-[33px] pb-5 responsive1200:mt-[20px] responsive1200:gap-[20px] responsive1200:flex-col ${modify && "flex-col gap-y-3"}`}
                    >
                        <div className="flex-1">
                            <div className="flex items-center responsive1200:flex-col responsive1200:items-start responsive1200:gap-[18px]">
                                <div className={`text-[18px] font-[500] w-full ${modify ? "max-w-[120px]" : "max-w-[160px]"}`}>
                                    수량 <span className="text-new text-[16px] font-normal">*</span>
                                </div>
                                <div className="flex flex-col gap-2 responsive1200:w-full">
                                    <Input
                                        className={`w-full ${modify ? "max-w-[120px]" : "max-w-[230px] responsive1200:max-w-full"}`}
                                        unit="개"
                                        unitPadding="pr-9"
                                        value={data.count}
                                        onChange={e => {
                                            const numericValue = e.target.value.replace(/[^0-9]/g, "");
                                            const number = parseInt(numericValue || "0", 10);

                                            if (number > 999) return;

                                            setData((prev: any) => ({ ...prev, count: numericValue }));

                                            if (isSubmitting) {
                                                const isInValidCount = numericValue.trim().length === 0;
                                                setErrors(prev => ({ ...prev, count: isInValidCount }));
                                            } else {
                                                setErrors(prev => ({ ...prev, count: false }));
                                            }
                                        }}
                                        error={errors?.count}
                                    />
                                    {errors?.count && <p className="text-[15px] text-[#ff4242]">{errorMessages?.count}</p>}
                                </div>
                            </div>
                            <p
                                // className={`text-new text-[15px] ${modify && data.pickup !== 2 && data.payment !== 3 && data.payment !== 4 ? "mt-[52px]" : "mt-5 responsive1200:mt-1"}`}
                                className={`text-new text-[15px] mt-3 responsive1200:mt-1`}
                            >
                                * 입력하신 수량만큼 운송장 생성 및 청구됩니다.
                            </p>
                        </div>
                    </div>
                    {data.payment === 4 && (
                        <div className="border-t py-5 flex flex-col gap-[18px]">
                            <div className={`flex ${modify ? "flex-col gap-1" : "items-center"}`}>
                                <div className="flex items-center relative">
                                    <div className="text-[18px] font-[500] w-[160px]">
                                        요금처 상호명 <span className="text-new text-[16px] font-normal">*</span>
                                    </div>
                                    <Input
                                        placeholder="주소록 검색"
                                        className={`${modify ? "w-[440px]" : "w-[360px]"}`}
                                        value={data.name}
                                        onChange={e => {
                                            setData((prev: any) => ({ ...prev, name: e.target.value }));
                                            mutate({ searchText: e.target.value });
                                            setThirdPartyIsFocused(true);
                                            setErrors(prev => ({ ...prev, name: false }));
                                        }}
                                        onFocus={() => setThirdPartyIsFocused(true)}
                                        onKeyDown={handleThirdPartyKeyDown}
                                        error={errors?.name}
                                    />
                                    {thirdPartyIsFocused &&
                                        data.name.length >= 1 &&
                                        addressBookSearchData &&
                                        addressBookSearchData.length > 0 && (
                                            <div className="max-w-[360px] absolute top-[56px] left-[159px] z-10 border bg-white rounded-lg shadow w-full">
                                                <button
                                                    onClick={() => setThirdPartyIsFocused(false)}
                                                    className="absolute right-0 top-0 w-6 h-6 hover:bg-black/10 flex justify-center items-center rounded-full"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                                <div className={` max-h-[400px] overflow-y-auto w-[360px]`}>
                                                    {addressBookSearchData.map((company, index) => {
                                                        const displayInfo = `${company.a_company} | ${company.a_tel} | ${company.a_address}`;

                                                        return (
                                                            <div
                                                                key={`${company.a_code}-${index}`}
                                                                ref={el => (thirdPartyRefs.current[index] = el)}
                                                                className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-[16px] ${
                                                                    thirdPartyFocusedIndex === index ? "bg-gray-200" : "hover:bg-gray-100"
                                                                }`}
                                                                onClick={() => {
                                                                    setData({
                                                                        ...data,
                                                                        name: company.a_company,
                                                                        tel: company.a_tel,
                                                                        postcode: company.a_post,
                                                                        address: company.a_address,
                                                                        addressDetail: company.a_address_detail,
                                                                        s_pay_company_code: company.a_code,
                                                                    });
                                                                    setThirdPartyIsFocused(false);
                                                                }}
                                                            >
                                                                {displayInfo}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                </div>

                                <div className={`${modify ? "ml-[435px]" : "ml-2"} gap-2 flex`}>
                                    <Button color="new" onClick={() => setShowAddressBook(true)}>
                                        주소록
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setData((prev: any) => ({
                                                ...prev,
                                                name: "",
                                                tel: "",
                                                manager: "",
                                                postcode: "",
                                                address: "",
                                                addressDetail: "",
                                            }));
                                            setShowPostcode(false);
                                        }}
                                    >
                                        주소리셋
                                    </Button>
                                </div>
                            </div>
                            <div className="flex items-center">
                                <div className="text-[18px] font-[500] w-[160px]">
                                    요금처 연락처 <span className="text-new text-[16px] font-normal">*</span>
                                </div>
                                <Input
                                    maxLength={13}
                                    placeholder="연락처를 입력해주세요."
                                    className={`${modify ? "w-[440px]" : "w-[360px]"}`}
                                    value={data.tel}
                                    onChange={e => {
                                        setData((prev: any) => ({ ...prev, tel: e.target.value }));
                                        setErrors(prev => ({ ...prev, tel: false }));
                                    }}
                                    error={errors?.tel}
                                />
                            </div>
                            <div className="flex items-center">
                                <div className="text-[18px] font-[500] w-[160px]">
                                    요금처 담당자 <span className="text-new text-[16px] font-normal">*</span>
                                </div>
                                <Input
                                    placeholder="담당자를 입력해주세요."
                                    className={`${modify ? "w-[440px]" : "w-[360px]"}`}
                                    value={data.manager}
                                    onChange={e => {
                                        setData((prev: any) => ({ ...prev, manager: e.target.value }));
                                        setErrors(prev => ({ ...prev, manager: false }));
                                    }}
                                    error={errors?.manager}
                                />
                            </div>
                            <div className="flex flex-col gap-1 relative">
                                <div className="text-[18px] font-[500] w-[160px] mb-1">
                                    요금처 주소 <span className="text-new text-[16px] font-normal">*</span>
                                </div>
                                <div className={`${modify ? "w-[600px]" : "w-[520px]"} flex gap-1.5`}>
                                    <Input
                                        disabled
                                        onClick={() => setShowPostcode(true)}
                                        className="w-full"
                                        value={data.postcode}
                                        onChange={e => {
                                            setData((prev: any) => ({ ...prev, postcode: e.target.value }));
                                            setErrors(prev => ({ ...prev, postcode: false }));
                                        }}
                                        error={errors?.postcode}
                                    />
                                    <button
                                        onClick={() => setShowPostcode(true)}
                                        disabled={data.postcode.length > 0 ? true : false}
                                        className="disabled:opacity-30 disabled:cursor-not-allowed min-w-[123px] border rounded-lg border-[#1B1C1E] text-[18px] font-[500]"
                                    >
                                        우편번호
                                    </button>
                                </div>
                                <Input
                                    disabled
                                    onClick={() => setShowPostcode(true)}
                                    className={`${modify ? "w-[600px]" : "w-[520px]"}`}
                                    value={data.address}
                                    onChange={e => {
                                        setData((prev: any) => ({ ...prev, address: e.target.value }));
                                        setErrors(prev => ({ ...prev, address: false }));
                                    }}
                                    error={errors?.address}
                                />
                                <Input
                                    className={`${modify ? "w-[600px]" : "w-[520px]"}`}
                                    placeholder="상세주소 입력"
                                    value={data.addressDetail}
                                    onChange={e => {
                                        setData((prev: any) => ({ ...prev, addressDetail: e.target.value }));
                                        setErrors(prev => ({ ...prev, addressDetail: false }));
                                    }}
                                    error={errors?.addressDetail}
                                />
                                {showPostcode && data.postcode.length === 0 && (
                                    <div className="absolute z-[100] mt-[34px] top-[62px] bg-white border w-[520px] rounded-lg shadow-lg overflow-hidden">
                                        <div className="px-6 py-5 text-2xl font-bold border-b border-gray3 flex items-center justify-between">
                                            <p>우편번호 찾기</p>
                                            <Close onClick={() => setShowPostcode(false)} className="cursor-pointer" />
                                        </div>
                                        <DaumPostcodeEmbed className="min-h-[470px]" onComplete={handleAddress} />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {showAddressBook &&
                        createPortal(
                            <div className="fixed inset-0 z-[200] bg-black bg-opacity-40 flex items-center justify-center">
                                <AddressBook onSelect={handleAddressSelect} onClose={() => setShowAddressBook(false)} />
                            </div>,
                            document.body,
                        )}
                </div>

                {/* <Drawer>
                    <DrawerTrigger tabIndex={-1} ref={drawerTriggerRef} className="absolute opacity-0 w-0 h-0" />
                    <DrawerContent className="bg-white [&_.rdp-root]:flex [&_.rdp-root]:justify-center max-h-[80dvh] overlflow-hidden">
                        <DrawerHeader>
                            <DrawerTitle hidden />
                            <DrawerDescription hidden />
                        </DrawerHeader>
                        <div className="px-[4%] pb-5 overflow-auto">{shippingAndPaymentInfo}</div>
                    </DrawerContent>
                </Drawer> */}

                <Dialog>
                    <DialogTrigger ref={drawerTriggerRef} className="absolute" />

                    <DialogContent className="bg-white p-0 gap-0 w-full max-w-[560px] responsive700:max-w-[85%] rounded-lg shadow-lg overflow-hidden">
                        <DialogHeader className="px-5 py-5">
                            <DialogTitle className="text-2xl text-center">발송 및 지불 방법</DialogTitle>
                            <DialogDescription hidden />
                        </DialogHeader>
                        <div className="px-5 pt-8 pb-10 max-h-[60dvh] overflow-y-auto flex-1 w-full flex flex-col gap-y-8">
                            {shippingAndPaymentInfo}
                        </div>
                    </DialogContent>
                </Dialog>

                {showOrderGuide &&
                    createPortal(
                        <div className="fixed pb-[100px] flex flex-col justify-start items-baseline overflow-hidden inset-0 z-[200] bg-white">
                            <button
                                onClick={() => setShowOrderGuide(false)}
                                className="ml-auto mt-3 mr-5 w-[36px] h-[36px] rounded-full bg-[#eeeeee] flex items-center justify-center"
                            >
                                <X className="h-8 w-8" />
                                <span className="sr-only">닫기</span>
                            </button>
                            <div className="overflow-auto">
                                <OrderGuide />
                            </div>
                        </div>,
                        document.body,
                    )}
            </>
        );
    },
);

export default PayMethod;
